import { IonButton, IonCol, IonRow } from "@ionic/react";
import { useCallback, useEffect, useRef, useState } from "react";
import FilterList from "../../../FilterList/FilterList";
import { FilterState } from "../../../FilterList/types";
import List from "../../../List/List";
import { pageRange } from "../../../List/Pagination/pagerange";
import { useFetchSystemElements, useModifySystemElement } from "../../../ReactData/system";
import { Filter } from "../types/reportTypes";
import { usePersistentState } from "../../../Persistence";
import
{
  PaginatedResult,
  PaginationRequest,
  SystemElement,
} from "@ews/react-data";
import { useL10n } from "@ews/react-localization-context";
import { LoopInputProps, xmlTypeRecord } from "./types";
import SettingsSelect from "../../../Settings/SettingsComponents/SettingsSelect/SettingsSelect";
import SettingsSelectOption from "../../../Settings/SettingsComponents/SettingsSelectOption/SettingsSelectOption";
import { NumberGroup } from "@ews/zlt-events";
import { useHistory } from "react-router-dom";
import { useDialog } from "../../../Dialog";

type PersistenceFilter = Filter<{}> & FilterState;
type SystemElementMetaData = Omit<SystemElement, 'categoryType'> & {
  categoryType: SystemElement['categoryType'][number];
} & { index: number; parent: SystemElement; };

const ElementList: React.FC<LoopInputProps> = ({
  systemId,
  onIsOpen,
  filter,
}) =>
{
  const fetchData = useFetchSystemElements();
  const modifyData = useModifySystemElement();
  const [originalElements, setOriginalElements] = useState<SystemElementMetaData[]>([] as SystemElementMetaData[]);
  const [editElements, setEditElements] = useState<SystemElementMetaData[]>([] as SystemElementMetaData[]);
  const [changesMadeFlag, setChangesMadeFlag] = useState<boolean>(false);
  const dialog = useDialog();
  const history = useHistory();

  const page = useRef<number | undefined>();
  const numberOfPages = useRef<number | undefined>();

  const { translate: t } = useL10n();
  const standardFilter: Filter<{}, keyof SystemElement> = {
    sortCriteria: "numberGroup,groupId,id" as keyof SystemElement,
    sortOrder: "ASC",
    pageSize: pageRange[1]
  };
  useEffect(() =>
  {
  }, [editElements]);
  const [currentState, setState] = usePersistentState<PersistenceFilter>(
    "system:elementslist",
    { ...standardFilter } as PersistenceFilter
  );

  const getFilteredData = useCallback(
    async (id: string, filter: PaginationRequest) =>
    {
      const response = await fetchData(id, filter);
      if (!response) return;

      const { results, currentPage, pageSize, totalCountOfPages } =
        response as PaginatedResult<SystemElement>;

      page.current = currentPage;
      numberOfPages.current = totalCountOfPages;

      transformAndSetRows(results);
    },
    [fetchData]
  );

  useEffect(() =>
  {


    setState({ ...currentState, isFilterOpen: true });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() =>
  {
    if ("isFilterOpen" in currentState) {
      onIsOpen && onIsOpen(currentState.isFilterOpen || false, false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() =>
  {
    if (systemId) {
      getFilteredData(systemId, currentState);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState, systemId]);

  // 1) Block browser refresh / close
  useEffect(() =>
  {
    if (!changesMadeFlag) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) =>
    {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);

  }, [changesMadeFlag]);


  // 2) Block go back navigation
  useEffect(() =>
  {
    if (!changesMadeFlag) return;

    const runDialog = async () =>
    {
      return await dialog({
        title: t('Are you sure you want to navigate back?'),
        message: t('You will loose all unsaved changes you have made'),
        confirmButtonTitle: t('[SID_YES]'),
        cancelButtonTitle: t('COM_BANNERS_CANCEL')
      });
    };

    const unblock = history.block((retry: any) =>
    {
      (async () =>
      {
        const confirmed = await runDialog();
        if (confirmed) {
          unblock();
          if (typeof retry === "function") {
            retry();
          }
          else {
            history.push(retry?.to || "/en/system");
          }
        }
      })();

      return false;
    });

    return () => unblock();

  }, [history, changesMadeFlag]);

  const handleDropdownChange = (
    key: "category" | "type",
    value: string,
    row: SystemElementMetaData
  ) =>
  {
    setChangesMadeFlag(true);
    setEditElements((prev) =>
      prev.map((el) =>
        el.groupId === row.groupId &&
          el.numberGroup === row.numberGroup &&
          el.id === row.id &&
          el.index === row.index
          ? {
            ...el,
            categoryType: {
              ...el.categoryType,
              [key]: value,
            },
          }
          : el
      )
    );
  };

  const handleAddElement = (row: SystemElementMetaData) =>
  {
    setChangesMadeFlag(true);
    setEditElements((prev) =>
    {
      const index = prev.findIndex(
        (el) =>
          el.groupId === row.groupId &&
          el.id === row.id &&
          el.numberGroup === row.numberGroup &&
          el.index === row.index
      );
      if (index === -1) return prev;

      const rowsOfParent = prev
        .filter(el =>
          el.groupId === row.groupId &&
          el.id === row.id &&
          el.numberGroup === row.numberGroup &&
          el.parent === row.parent
        );

      const lastRow = rowsOfParent.reduce((max, curr) => (curr.index > max.index ? curr : max));

      const nextChildIndex =
        Math.max(
          0,
          ...prev
            .filter((el) => el.parent === row.parent && el.index > 0)
            .map((el) => el.index)
        ) + 1;


      const newElement: SystemElementMetaData = {
        ...row,
        index: nextChildIndex,
        parent: row.parent,
        categoryType: lastRow.categoryType
      };


      const updated = [
        ...prev.slice(0, index + 1),
        newElement,
        ...prev.slice(index + 1),
      ];
      const parentIndices = updated
        .map((el, i) => ({ el, i }))
        .filter(x => x.el.parent === row.parent)
        .map(x => x.i);


      const sortedSiblings = parentIndices
        .map(i => updated[i])
        .sort((a, b) => a.index - b.index);

      parentIndices.forEach((pos, i) =>
      {
        updated[pos] = sortedSiblings[i];
      });

      return updated;

    });
  };

  const handleDeleteElement = (row: SystemElementMetaData) =>
  {
    setEditElements((prev) =>
    {
      const filtered = prev.filter((el) =>
      {
        const match =
          Number(el.numberGroup) === Number(row.numberGroup) &&
          Number(el.groupId) === Number(row.groupId) &&
          Number(el.id) === Number(row.id) &&
          Number(el.index) === Number(row.index);

        return !match;
      });
      return filtered;
    });
  };


  const transformAndSetRows = (elements: SystemElement[]) =>
  {
    const converted = [...elements].flatMap((element) =>
      (element.categoryType ?? []).map((ct, i) =>
      {
        const { categoryType, ...rest } = element;
        return {
          ...rest,
          categoryType: ct,
          index: i,
          parent: element,
        };
      })
    );

    setEditElements(converted);
    setOriginalElements(converted);
  };


  const handleSaveChanges = async () =>
  {
    const rowsChangedOrAdded = editElements.filter((e) =>
    {
      const originalElement = originalElements.find((o) => o.id === e.id &&
        o.groupId === e.groupId && e.numberGroup === o.numberGroup && e.index === o.index);
      if (!originalElement) return true;

      const originalCategory = originalElement.categoryType;
      const editedCategory = e.categoryType;

      const result = originalCategory.category !== editedCategory.category ||
        originalCategory.type !== editedCategory.type;

      return (
        result
      );
    });

    const rowsDeleted = originalElements.filter((o) =>
    {
      const stillExists = editElements.some(
        (e) =>
          e.id === o.id &&
          e.groupId === o.groupId &&
          e.numberGroup === o.numberGroup &&
          e.index === o.index
      );
      return !stillExists;
    });

    const updatedSystemElements: SystemElement[] = [];

    rowsChangedOrAdded.forEach((item) =>
    {
      const exists = updatedSystemElements.some(
        entry =>
          entry.numberGroup === item.numberGroup &&
          entry.groupId === item.groupId &&
          entry.id === item.id

      );

      if (exists) return;

      const allOfParentElement = editElements.filter(entry => entry.parent === item.parent);
      let categoryType: { category: string; type: string; }[] = [];
      allOfParentElement.forEach(element =>
      {
        categoryType.push(element.categoryType);
      });

      const updatedElement = { ...item.parent, categoryType };
      updatedSystemElements.push(updatedElement);
    });

    rowsDeleted.forEach(item =>
    {
      const allOfParentElement = editElements.filter(
        (entry) =>
          entry.parent === item.parent &&
          !(
            entry.id === item.id &&
            entry.groupId === item.groupId &&
            entry.numberGroup === item.numberGroup &&
            entry.index === item.index
          )
      );

      let categoryType: { category: string; type: string; }[] = [];
      allOfParentElement.forEach(element =>
      {
        categoryType.push(element.categoryType);
      });

      const updatedElement = { ...item.parent, categoryType };

      const existingIndex = updatedSystemElements.findIndex(
        (entry) =>
          entry.numberGroup === updatedElement.numberGroup &&
          entry.groupId === updatedElement.groupId &&
          entry.id === updatedElement.id
      );

      if (existingIndex === -1) {
        updatedSystemElements.push(updatedElement);
      } else {
        updatedSystemElements[existingIndex] = updatedElement;
      }
    });

    for (const element of updatedSystemElements) {
      await modifyData(
        element.systemId as string,
        element.numberGroup as number,
        element.groupId as number,
        element.id as number,
        element
      );
    }
    getFilteredData(systemId, currentState);
    setChangesMadeFlag(false);
  };

  return (
    <>
      <FilterList
        onReset={() =>
        {
          const { isFilterOpen, isDetailOpen } = currentState;
          setState({ ...standardFilter, isFilterOpen, isDetailOpen });
        }}
        isOpen={filter?.isOpen}
        onIsOpen={(state) =>
        {
          onIsOpen && onIsOpen(state, true);
        }}
        onDetailOpen={(isDetailOpen) =>
        {
          if (isDetailOpen) {
            currentState.isDetailOpen = isDetailOpen;
          } else {
            delete currentState.isDetailOpen;
          }
          setState({ ...currentState });
        }}
        noPrint
      >

        <IonCol>
          <IonRow class="ion-justify-content-end">
            <IonButton color={"success"} disabled={!changesMadeFlag} onClick={() => { handleSaveChanges(); }}>
              {t("COM_FACPMANAGEMENT_USER_SAVE")}
            </IonButton>
          </IonRow>
        </IonCol>
      </FilterList>

      <List
        rows={editElements}
        style={{ maxHeight: "80vh" }}

        currentPage={page.current}
        numberOfPages={numberOfPages.current}
        pageSize={currentState.pageSize}
        onPageChange={(page) => setState({ ...currentState, page })}
        onPageSize={(pageSize) => setState({ ...currentState, pageSize })}
        sort={
          currentState.sortCriteria
            ? {
              key: currentState.sortCriteria,
              direction: currentState.sortOrder || "ASC",
            }
            : undefined
        }
        /* onMenu={(id: number, onMenuToggle: () => void) => (
          <IonList>
            <MenuLink
              title="COM_LOCALISE_ACCESS_EDIT"
              icon={pencilOutline}
              onClick={() => {
                const foundPsu = elements.find((data) => {
                  return data.id === id;
                });
                if (foundPsu) {
                  setCurrentElement(foundPsu);
                  setIsOpen(true);
                }
                onMenuToggle();
                return null;
              }}
            />
            <MenuLink
              title="COM_LOCALISE_ACCESS_DELETE"
              icon={trash}
              onClick={() => {
                const foundPsu = elements.find((data) => data.id === id);

                if (foundPsu) {

                }
                onMenuToggle();
                return null;
              }}
            />
          </IonList>
        )} */
        //maxHeight={mobileView ? undefined : filterDetailsOpen ? '45vh' : "61vh"}
        headers={[
          {
            title: "Number Group Grp Id",
            key: "numberGroup,groupId",
            both: true,
            child: (row) => row.index === 0 ? <span>{NumberGroup[row.numberGroup!]} {row.groupId}</span> : null
          },
          /*     {
                title: "Group Id",
                key: "groupId",
                both: true,
                child: (row) =>
                {
                  return row.index === 0 ? <span>{row.groupId}</span> : null;
                }
              }, */
          {
            title: "Id",
            key: "id",
            both: true,
            child: (row) =>
            {
              return row.index === 0 ? <span>{row.id}</span> : null;
            }
          },
          /*     {
                title: "index",
                key: "index",
                both: true,
                child: (row) =>
                {
                  return <span>{row.index}</span>;
                }
              }, */
          {
            title: "Category",
            both: true,
            key: "categoryType" as keyof SystemElement,
            child: (row) =>
            {
              const keys = Object.keys(xmlTypeRecord);

              return (
                <SettingsSelect
                  text=""
                  onSelect={(value) => handleDropdownChange("category", value, row)}
                  value={row.categoryType.category}
                >
                  {keys.map((key, index) => (
                    <SettingsSelectOption key={index} text={key} value={key} />
                  ))}
                </SettingsSelect>
              );
            },
          }
          ,
          {
            title: "TypeText",
            both: true,
            key: "categoryType" as keyof SystemElement,
            child: (row) =>
            {
              const entries = xmlTypeRecord[row.categoryType.category];
              return (
                <SettingsSelect
                  text=""
                  value={row.categoryType.type}
                  onSelect={(value) => handleDropdownChange("type", value, row)}
                >
                  {entries?.LST_FI700 && (
                    <>
                      <SettingsSelectOption
                        key="LST_FI700"
                        text="LST_FI700"
                        value="LST_FI700"
                        disabled={true}
                      />
                      {entries.LST_FI700.map((item, index) => (
                        <SettingsSelectOption
                          key={index}
                          text={item}
                          value={item}
                        />
                      ))}
                    </>
                  )}
                  {entries?.Apollo && (
                    <>
                      <SettingsSelectOption
                        key="Apollo"
                        text="Apollo"
                        value="Apollo"
                        disabled={true}
                      />
                      {entries.Apollo.map((item, index) => (
                        <SettingsSelectOption
                          key={index}
                          text={item}
                          value={item}
                        />
                      ))}
                    </>
                  )}
                  {entries?.SystemSensor && (
                    <>
                      <SettingsSelectOption
                        key="SystemSensor"
                        text="SystemSensor"
                        value="SystemSensor"
                        disabled={true}
                      />
                      {entries.SystemSensor.map((item, index) => (
                        <SettingsSelectOption
                          key={index}
                          text={item}
                          value={item}
                        />
                      ))}
                    </>
                  )}
                  {entries?.none && (
                    <>
                      <SettingsSelectOption
                        key="none"
                        text="none"
                        value="none"
                        disabled={true}
                      />
                      {entries.none.map((item, index) => (
                        <SettingsSelectOption
                          key={index}
                          text={item}
                          value={item}
                        />
                      ))}
                    </>
                  )}

                </SettingsSelect>
              );
            },
          }
          ,
          {
            title: 'Action', key: 'categoryType', both: true,
            child: (row) =>
            {

              return (
                row.index === 0 ?
                  <IonButton onClick={() => handleAddElement(row)}>Add</IonButton>
                  : <IonButton onClick={() => handleDeleteElement(row)}>Delete</IonButton>
              );
            },
          }

        ]}
      />


    </>

  );
};

export default ElementList;
